import React from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent } from '../ui/card';

const features = [
  // Core Synchronization Features
  {
    icon: "🎯",
    title: "Synchronized Game Data",
    description: "Synchronized critical game data across all devices — no one else can match our capability! No QR codes or configuration needed."
  },
  {
    icon: "🔔",
    title: "Simultaneous Alerts",
    description: "Receive level change alerts simultaneously on all connected devices - this is a game changer! Every device gets next level alerts."
  },
  {
    icon: "🔊",
    title: "Audio Broadcasts",
    description: "Game restart audio broadcast when game resumes after breaks. Everyone receives audio notifications when the game restarts!"
  },
  {
    icon: "🔋",
    title: "Low Power Detection",
    description: "Smart low power mode detection - no more false/missed alerts. Notifications not sent if device is in low power mode."
  },
  
  // Game Management
  {
    icon: "🎮",
    title: "Three Game Types",
    description: "Instant games (start immediately), Weekly recurring games, and One-off scheduled games. Complete flexibility for any poker event."
  },
  {
    icon: "⏰",
    title: "Centrally Controlled Timers",
    description: "Automatic blind level increases with centrally controlled timers. Game breaks support with pause/resume functionality."
  },
  {
    icon: "📋",
    title: "Game Audit Log",
    description: "Complete chronological audit of events for admin users. Track all changes including accidental clock adjustments."
  },
  {
    icon: "📊",
    title: "Connection Stats",
    description: "Real-time game connection statistics available from game screen. Monitor performance and connectivity."
  },
  
  // Location & Discovery
  {
    icon: "📍",
    title: "GPS Game Finder",
    description: "Find nearby games within 200 miles using GPS. Filter games by distance with separate running/upcoming sections."
  },
  {
    icon: "🏢",
    title: "Venue Registration",
    description: "Enter zip/postcode for GPS searchable games. Map links to display club locations with one-click access."
  },
  {
    icon: "🔍",
    title: "Smart Discovery",
    description: "One-click access to critical game information. Separate sections for running and upcoming games with detailed filtering."
  },
  
  // Community & Social
  {
    icon: "⭐",
    title: "Player Reviews",
    description: "Rate and review each individual game to help build the poker community and improve game quality for everyone."
  },
  {
    icon: "📸",
    title: "Photo Albums",
    description: "Game photo albums and photo capture during games. Winner photo capture and display for memorable moments."
  },
  {
    icon: "👤",
    title: "User Personalization",
    description: "Change your display name for personalization. Quick registration with Apple or Google login integration."
  },
  {
    icon: "💬",
    title: "In-Game Feedback",
    description: "Send feedback directly from the game screen. Continuous improvement through community input and suggestions."
  },
  
  // Analytics & Reporting
  {
    icon: "📈",
    title: "League Tables",
    description: "Detailed league tables based on weekly game reports. Monthly, quarterly, and annual reports with in-depth insights."
  },
  {
    icon: "🏆",
    title: "Player Statistics",
    description: "Complete player statistics including placement in each game and prizes. Top 3 players summary on venue front page."
  },
  {
    icon: "📋",
    title: "Club Summary",
    description: "Club summary pages with upcoming games and completed game reports. Access all reports from venue summary page."
  },
  
  // Tools & Utilities
  {
    icon: "🧮",
    title: "Pot Split Utility",
    description: "Easy pot split calculations with our built-in utility tool. New tools section with useful poker tools."
  },
  {
    icon: "🛠️",
    title: "Club Management",
    description: "Club content management component for venue administrators. Comprehensive venue and user registration system."
  },
  {
    icon: "🔄",
    title: "Version Management",
    description: "Version check popup for updates. Refresh button on Venues page for error recovery and system maintenance."
  },
  
  // Performance & Security
  {
    icon: "⚡",
    title: "Performance Optimized",
    description: "Fine-tuned Java engine performance with minimized websocket traffic. Backend retry mechanisms for reliability."
  },
  {
    icon: "🎨",
    title: "Modern Interface",
    description: "Major frontend refresh with stylish look and improved layouts. Enhanced user experience with new preferences."
  },
  {
    icon: "🔐",
    title: "Security & Privacy",
    description: "Forgotten password functionality with secure login. No third-party data sharing - your privacy is protected."
  }
];

export default function FeaturesSection() {
  return (
    <div className="py-20 bg-black/20">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold mb-4 bg-gradient-to-r from-purple-400 to-purple-600 bg-clip-text text-transparent">Powerful Features</h2>
          <p className="text-xl text-gray-300">Everything you need for the perfect poker game</p>
        </div>
        
        <div className="space-y-12">
          {/* Core Synchronization Features */}
          <div>
            <h3 className="text-2xl font-bold mb-8 text-center text-purple-400">🎯 Core Synchronization</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {features.slice(0, 4).map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Card className="bg-black/40 border-purple-500/30 hover:border-purple-500/60 hover:bg-black/60 transition-all duration-300 h-full shadow-2xl">
                    <CardContent className="p-6 text-center">
                      <div className="text-4xl mb-4">{feature.icon}</div>
                      <h4 className="text-lg font-semibold mb-3 text-purple-400">{feature.title}</h4>
                      <p className="text-gray-300 text-sm">{feature.description}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Game Management */}
          <div>
            <h3 className="text-2xl font-bold mb-8 text-center text-purple-400">🎮 Game Management</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {features.slice(4, 8).map((feature, index) => (
                <motion.div
                  key={index + 4}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Card className="bg-black/40 border-purple-500/30 hover:border-purple-500/60 hover:bg-black/60 transition-all duration-300 h-full shadow-2xl">
                    <CardContent className="p-6 text-center">
                      <div className="text-4xl mb-4">{feature.icon}</div>
                      <h4 className="text-lg font-semibold mb-3 text-purple-400">{feature.title}</h4>
                      <p className="text-gray-300 text-sm">{feature.description}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Location & Discovery */}
          <div>
            <h3 className="text-2xl font-bold mb-8 text-center text-purple-400">📍 Location & Discovery</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {features.slice(8, 11).map((feature, index) => (
                <motion.div
                  key={index + 8}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Card className="bg-black/40 border-purple-500/30 hover:border-purple-500/60 hover:bg-black/60 transition-all duration-300 h-full shadow-2xl">
                    <CardContent className="p-6 text-center">
                      <div className="text-4xl mb-4">{feature.icon}</div>
                      <h4 className="text-lg font-semibold mb-3 text-purple-400">{feature.title}</h4>
                      <p className="text-gray-300 text-sm">{feature.description}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Community & Social */}
          <div>
            <h3 className="text-2xl font-bold mb-8 text-center text-purple-400">👥 Community & Social</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {features.slice(11, 15).map((feature, index) => (
                <motion.div
                  key={index + 11}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Card className="bg-black/40 border-purple-500/30 hover:border-purple-500/60 hover:bg-black/60 transition-all duration-300 h-full shadow-2xl">
                    <CardContent className="p-6 text-center">
                      <div className="text-4xl mb-4">{feature.icon}</div>
                      <h4 className="text-lg font-semibold mb-3 text-purple-400">{feature.title}</h4>
                      <p className="text-gray-300 text-sm">{feature.description}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Analytics & Reporting */}
          <div>
            <h3 className="text-2xl font-bold mb-8 text-center text-purple-400">📊 Analytics & Reporting</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {features.slice(15, 18).map((feature, index) => (
                <motion.div
                  key={index + 15}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Card className="bg-black/40 border-purple-500/30 hover:border-purple-500/60 hover:bg-black/60 transition-all duration-300 h-full shadow-2xl">
                    <CardContent className="p-6 text-center">
                      <div className="text-4xl mb-4">{feature.icon}</div>
                      <h4 className="text-lg font-semibold mb-3 text-purple-400">{feature.title}</h4>
                      <p className="text-gray-300 text-sm">{feature.description}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Tools & Utilities */}
          <div>
            <h3 className="text-2xl font-bold mb-8 text-center text-purple-400">🛠️ Tools & Utilities</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {features.slice(18, 21).map((feature, index) => (
                <motion.div
                  key={index + 18}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Card className="bg-black/40 border-purple-500/30 hover:border-purple-500/60 hover:bg-black/60 transition-all duration-300 h-full shadow-2xl">
                    <CardContent className="p-6 text-center">
                      <div className="text-4xl mb-4">{feature.icon}</div>
                      <h4 className="text-lg font-semibold mb-3 text-purple-400">{feature.title}</h4>
                      <p className="text-gray-300 text-sm">{feature.description}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Performance & Security */}
          <div>
            <h3 className="text-2xl font-bold mb-8 text-center text-purple-400">⚡ Performance & Security</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {features.slice(21, 24).map((feature, index) => (
                <motion.div
                  key={index + 21}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Card className="bg-black/40 border-purple-500/30 hover:border-purple-500/60 hover:bg-black/60 transition-all duration-300 h-full shadow-2xl">
                    <CardContent className="p-6 text-center">
                      <div className="text-4xl mb-4">{feature.icon}</div>
                      <h4 className="text-lg font-semibold mb-3 text-purple-400">{feature.title}</h4>
                      <p className="text-gray-300 text-sm">{feature.description}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
